import React, { useState, useEffect, useCallback } from "react";

/**
 * A progressive-loading image designed for hero mode tiles where the <img>
 * must remain a direct child of a `position: relative` container (no wrapper
 * element). Renders the <img> + a rounded shimmer placeholder overlay as
 * siblings via a Fragment. The placeholder positions itself over the parent
 * container.
 *
 * Use this inside hero modes where CSS relies on the img being a direct child.
 * For standalone images where a wrapper is acceptable, use ProgressiveImage.
 */
const ProgressiveHeroImage = ({
  src,
  alt,
  loading,
  draggable,
  className,
  style,
  onLoad: onLoadProp,
  ...rest
}) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!src) return;
    setLoaded(false);
    const img = new window.Image();
    img.src = src;
    if (img.complete && img.naturalWidth > 0) {
      setLoaded(true);
    } else {
      img.onload = () => setLoaded(true);
      img.onerror = () => setLoaded(true);
    }
    return () => { img.onload = null; img.onerror = null; };
  }, [src]);

  const handleLoad = useCallback(
    (e) => {
      setLoaded(true);
      if (onLoadProp) onLoadProp(e);
    },
    [onLoadProp]
  );

  // Honour a caller-supplied ``opacity`` (e.g. a user's image-style
  // opacity filter) by multiplying it into the load cross-fade instead of
  // letting the fade's hardcoded ``1`` clobber it once the image loads.
  const targetOpacity =
    style && Number.isFinite(Number(style.opacity)) ? Number(style.opacity) : 1;

  return (
    <>
      <img
        src={src}
        alt={alt}
        loading={loading}
        draggable={draggable}
        className={className}
        style={{
          ...style,
          opacity: loaded ? targetOpacity : 0,
          transition: "opacity 0.35s ease-in-out",
        }}
        onLoad={handleLoad}
        {...rest}
      />
      {src && (
        <div
          className="progressive-hero-placeholder"
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(200, 200, 200, 0.15)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            borderRadius: "inherit",
            overflow: "hidden",
            pointerEvents: "none",
            zIndex: 1,
            opacity: loaded ? 0 : 1,
            transition: "opacity 0.3s ease-out",
          }}
          aria-hidden="true"
        >
          <span
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)",
              animation: "progressive-img-shimmer 1.5s ease-in-out infinite",
            }}
          />
        </div>
      )}
    </>
  );
};

export default ProgressiveHeroImage;
